import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Send } from 'lucide-react';
import { z } from 'zod';
import { AVATARS, changePasswordBody, deleteMeBody, updateMeBody, type AuthResponse, type MeDto, type ProfileDto } from '@mafia/core';
import { api } from '@/lib/api';
import { useFormSubmit } from '@/lib/forms';
import { formatDate } from '@/lib/format';
import { useAuth } from '@/store/auth';
import { useGame } from '@/store/game';
import { Avatar } from '@/components/Avatar';
import { Alert, Badge, Button, Card, Field, Input, Modal, PageSpinner } from '@/components/ui';
import { ProfileStats } from '@/components/Stats';

export function MyProfileRedirect() {
  const user = useAuth((s) => s.user)!;
  return <Navigate to={`/profile/${user.id}`} replace />;
}

export function ProfilePage() {
  const { id = '' } = useParams();
  const me = useAuth((s) => s.user)!;
  const profile = useQuery({ queryKey: ['profile', id], queryFn: () => api<ProfileDto>(`/api/users/${id}/profile`) });
  const isMe = id === me.id;

  if (profile.isLoading) return <PageSpinner />;
  if (profile.isError) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Alert>Игрок не найден</Alert>
      </div>
    );
  }
  const p = profile.data!;

  return (
    <div className="mx-auto max-w-4xl space-y-4 px-4 py-6 sm:py-10">
      <div className="flex items-center gap-4">
        <Avatar id={p.avatarId} name={p.nickname} size={72} />
        <div className="min-w-0">
          <h1 className="truncate font-display text-3xl">{p.nickname}</h1>
          <p className="text-sm text-zinc-500">
            @{p.login} · с {formatDate(p.createdAt)} {p.role === 'ADMIN' && <Badge tone="gold">админ</Badge>}
          </p>
          {p.telegram && (
            <a
              href={`https://t.me/${p.telegram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1.5 text-sm text-sky-400 hover:text-sky-300"
            >
              <Send size={14} /> @{p.telegram}
            </a>
          )}
        </div>
      </div>
      <ProfileStats profile={p} />
      {isMe && (
        <div className="grid gap-4 md:grid-cols-2">
          <EditProfile user={me} />
          <ChangePassword />
          <DeleteAccount />
        </div>
      )}
    </div>
  );
}

function EditProfile({ user }: { user: MeDto }) {
  const setUser = useAuth((s) => s.setUser);
  const qc = useQueryClient();
  const [nickname, setNickname] = useState(user.nickname);
  const [avatarId, setAvatar] = useState<string | null>(user.avatarId);
  const [telegram, setTelegram] = useState(user.telegram ? `@${user.telegram}` : '');
  const [saved, setSaved] = useState(false);
  const inLobby = useGame((s) => s.lobby !== null);
  const { errors, formError, pending, handle } = useFormSubmit(updateMeBody, async (body) => {
    const updated = await api<MeDto>('/api/me', { method: 'PATCH', body });
    setUser(updated);
    setTelegram(updated.telegram ? `@${updated.telegram}` : '');
    await qc.invalidateQueries({ queryKey: ['profile', user.id] });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  });

  return (
    <Card className="p-5">
      <h2 className="mb-4 font-display text-lg">Профиль</h2>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handle({ nickname, avatarId, telegram });
        }}
      >
        {formError && <Alert>{formError}</Alert>}
        <Field label="Никнейм" error={errors.nickname}>
          <Input value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={24} />
        </Field>
        <Field label="Telegram" error={errors.telegram}>
          <Input value={telegram} onChange={(e) => setTelegram(e.target.value)} placeholder="@username" maxLength={64} autoComplete="off" />
        </Field>
        <div>
          <span className="label">Аватарка</span>
          <div className="grid grid-cols-6 gap-2">
            <button
              type="button"
              onClick={() => setAvatar(null)}
              className={clsx('flex justify-center rounded-xl p-1.5', avatarId === null ? 'bg-white/10 ring-2 ring-gold-400' : 'hover:bg-white/5')}
              aria-label="Без аватарки"
            >
              <Avatar id={null} name={nickname || '?'} size={36} />
            </button>
            {AVATARS.map((a) => (
              <button
                type="button"
                key={a.id}
                onClick={() => setAvatar(a.id)}
                className={clsx('flex justify-center rounded-xl p-1.5', avatarId === a.id ? 'bg-white/10 ring-2 ring-gold-400' : 'hover:bg-white/5')}
                aria-label={a.id}
              >
                <Avatar id={a.id} name={nickname} size={36} />
              </button>
            ))}
          </div>
        </div>
        <Button type="submit" variant="gold" loading={pending}>
          {saved ? 'Сохранено ✓' : 'Сохранить'}
        </Button>
        {inLobby && <p className="text-xs text-zinc-500">Новый ник появится в комнате при следующем входе.</p>}
      </form>
    </Card>
  );
}

const passwordForm = changePasswordBody
  .extend({ confirm: z.string() })
  .refine((v) => v.newPassword === v.confirm, { message: 'Пароли не совпадают', path: ['confirm'] });

function ChangePassword() {
  const signedIn = useAuth((s) => s.signedIn);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [done, setDone] = useState(false);
  const { errors, formError, pending, handle } = useFormSubmit(passwordForm, async ({ confirm: _c, ...body }) => {
    signedIn(await api<AuthResponse>('/api/me/password', { method: 'POST', body }));
    setForm({ currentPassword: '', newPassword: '', confirm: '' });
    setDone(true);
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card className="p-5">
      <h2 className="mb-4 font-display text-lg">Смена пароля</h2>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setDone(false);
          void handle(form);
        }}
      >
        {formError && <Alert>{formError}</Alert>}
        {done && <Alert tone="green">Пароль изменён, остальные устройства разлогинены.</Alert>}
        <Field label="Текущий пароль" error={errors.currentPassword}>
          <Input type="password" autoComplete="current-password" value={form.currentPassword} onChange={set('currentPassword')} />
        </Field>
        <Field label="Новый пароль" error={errors.newPassword}>
          <Input type="password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} />
        </Field>
        <Field label="Повторите новый пароль" error={errors.confirm}>
          <Input type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
        </Field>
        <Button type="submit" variant="secondary" loading={pending}>
          Изменить пароль
        </Button>
      </form>
    </Card>
  );
}

function DeleteAccount() {
  const logout = useAuth((s) => s.logout);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const { errors, formError, pending, handle } = useFormSubmit(deleteMeBody, async (body) => {
    await api('/api/me/delete', { method: 'POST', body });
    await logout('Аккаунт удалён');
  });

  return (
    <Card className="border-blood-600/30 p-5 md:col-span-2">
      <h2 className="mb-2 font-display text-lg">Удаление аккаунта</h2>
      <p className="mb-4 text-sm text-zinc-400">
        Аккаунт, статистика и рейтинг удаляются навсегда. В чужих матчах вы останетесь как «Удалённый игрок».
      </p>
      <Button variant="danger" onClick={() => setOpen(true)}>
        Удалить аккаунт
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Удалить аккаунт?">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handle({ password });
          }}
        >
          <p className="text-sm text-zinc-400">Это нельзя отменить. Если вы сейчас в игре, вы из неё выйдете.</p>
          {formError && <Alert>{formError}</Alert>}
          <Field label="Пароль для подтверждения" error={errors.password}>
            <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          </Field>
          <Button type="submit" variant="danger" className="w-full" loading={pending} disabled={!password}>
            Удалить навсегда
          </Button>
        </form>
      </Modal>
    </Card>
  );
}
